import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { Ref, ref } from "vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { useStore } from "vuex";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AxiosError } from "axios";
import { LatestShipmentAddressModel } from "../types";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { useRouter } from "vue-router";
import { prepareShipmentPayload } from "../helpers";

interface ReturnHook {
  handleStartCardReplacingProcess: (
    input: LatestShipmentAddressModel
  ) => Promise<void>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
  errorMessage: Ref<string>;
}

export const useHandleReplaceCard = (): ReturnHook => {
  const { summary } = useCreditLineSummary();
  const isLoading = ref(false);
  const hasError = ref(false);
  const errorMessage = ref<string>("");
  const CreditLineService = new Service();
  const router = useRouter();
  const store = useStore();
  const reportCardOptions =
    store.getters["creditLine/getReportLostOrDamagedCard"];
  const errorMessageKey = "ERROR_REQUESTING_AGAIN_CARD";

  const handleRequestShipment = async (input: LatestShipmentAddressModel) => {
    isLoading.value = true;
    hasError.value = false;
    errorMessage.value = "";
    try {
      const payloadShipment = prepareShipmentPayload(
        summary.value,
        input,
        reportCardOptions,
        AuthenticationHelper?.getDeviceTag() || ""
      );
      const response = await CreditLineService.shipCreditCard(payloadShipment);
      if (!response || response?.status !== 200) {
        hasError.value = true;
        errorMessage.value = errorMessageKey;
        ErrorHandler.logCreditlineErrorResponse(response);
        DatadogLogger.Log(
          "Credit card shipping form error (replace card)",
          {
            deviceTag: AuthenticationHelper?.getDeviceTag() || "",
            creditLineId: summary.value?.creditLineId || "",
            errorMessage: response?.data?.message || "",
          },
          LogType.ERROR
        );
      } else {
        router.push({ name: "CreditCardView" });
      }
    } catch (error) {
      hasError.value = true;
      errorMessage.value = errorMessageKey;
      ErrorHandler.handleNetworkErrorAndLog(error as AxiosError);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    hasError,
    errorMessage,
    handleStartCardReplacingProcess: handleRequestShipment,
  };
};
